// Nine Lives '84 - heads-up display.
//
// DOM overlay: lives, level and score at the top, and a controls legend at
// the bottom that can be hidden with ? or by clicking it.

import type { Game } from '../sim/game';
import { FishbowlRoom } from '../sim/rooms/fishbowl';

const CONTROLS: [string, string][] = [
  ['← →', 'run'],
  ['↑ or Space', 'jump'],
  ['↓ + jump', 'drop off a fence or line'],
  ['↓', 'drink at a bowl; dive when swimming'],
  ['X', 'climb in / crawl / leave'],
  ['P', 'pause'],
  ['S', 'sound on/off'],
  ['R', 'retro mode'],
  ['Esc', 'quit to title'],
  ['/ or H', 'hide this (click it too)'],
];

export class Hud {
  private readonly root: HTMLElement;
  private readonly lives: HTMLElement;
  private readonly level: HTMLElement;
  private readonly task: HTMLElement;
  private readonly air: HTMLElement;
  private readonly score: HTMLElement;
  private readonly message: HTMLElement;
  private readonly legend: HTMLElement;
  private readonly gauges: HTMLElement;
  private lastKey = '';
  private splatUntil = 0;
  private splatText = '';
  paused = false;
  /** Touch devices have an on-screen pause button; the message names it. */
  touch = false;

  constructor(container: HTMLElement) {
    this.root = el('div', 'hud');
    container.appendChild(this.root);

    const gauges = el('div', 'hud-gauges');
    this.gauges = gauges;
    this.root.appendChild(gauges);
    this.lives = this.gauge(gauges, 'lives');
    this.level = this.gauge(gauges, 'level');
    this.task = this.gauge(gauges, 'room');
    this.air = this.gauge(gauges, 'air');
    this.air.className = 'hud-bar';
    this.air.appendChild(el('div', 'hud-fill'));
    this.score = el('div', 'hud-score');
    gauges.appendChild(this.score);

    this.message = el('div', 'hud-message hidden');
    this.root.appendChild(this.message);

    this.legend = el('div', 'hud-legend');
    for (const [key, what] of CONTROLS) {
      const row = el('div', 'hud-row');
      const k = el('kbd');
      k.textContent = key;
      const w = el('span');
      w.textContent = what;
      row.append(k, w);
      this.legend.appendChild(row);
    }
    this.legend.addEventListener('click', () => this.toggleLegend());
    this.root.appendChild(this.legend);
    window.addEventListener('keydown', (e) => {
      if (e.repeat || this.root.hidden) return;
      if (e.key === '?' || e.key === '/' || e.code === 'Slash' || e.code === 'KeyH') {
        e.preventDefault();
        this.toggleLegend();
      }
    });
  }

  /** Called with the new state whenever the legend is shown or hidden. */
  onLegend: ((shown: boolean) => void) | null = null;

  get legendShown(): boolean {
    return !this.legend.classList.contains('hidden');
  }

  setLegend(shown: boolean): void {
    this.legend.classList.toggle('hidden', !shown);
  }

  toggleLegend(): void {
    this.setLegend(!this.legendShown);
    this.onLegend?.(this.legendShown);
  }

  setVisible(v: boolean): void {
    this.root.hidden = !v;
  }

  /** Retro mode draws its own status line; hide the gauges. */
  setRetro(retro: boolean): void {
    this.gauges.hidden = retro;
  }

  /** Show a message for a while (room cleared, sound toggled). */
  flashMessage(text: string, ms = 1500): void {
    this.splatText = text;
    this.splatUntil = performance.now() + ms;
  }

  private gauge(parent: HTMLElement, label: string): HTMLElement {
    const box = el('div', 'hud-gauge');
    const l = el('span', 'hud-label');
    l.textContent = label;
    const v = el('div', 'hud-value');
    box.append(l, v);
    parent.appendChild(box);
    return v;
  }

  private pips(target: HTMLElement, count: number, max: number): void {
    const key = `${count}/${max}`;
    if (target.dataset.key === key) return;
    target.dataset.key = key;
    target.replaceChildren();
    target.className = 'hud-pips';
    for (let i = 0; i < max; i++) {
      target.appendChild(el('span', i < count ? 'pip on' : 'pip'));
    }
  }

  update(g: Game): void {
    this.pips(this.lives, g.lives, Math.max(g.lives, 9));
    this.level.textContent = String(g.level);
    this.score.textContent = String(g.score);
    const r = g.room;
    const task = r ? `${r.taskName} ${r.have}/${r.need} · ${r.seconds}s` : '';
    if (this.task.textContent !== task) this.task.textContent = task;
    (this.task.parentElement as HTMLElement).hidden = !r;
    const tank = r instanceof FishbowlRoom && r.phase === 'tank';
    (this.air.parentElement as HTMLElement).hidden = !tank;
    if (tank) {
      const pct = Math.max(0, Math.min(100, r.airFraction * 100));
      (this.air.firstElementChild as HTMLElement).style.width = `${pct.toFixed(1)}%`;
      this.air.classList.toggle('low', pct < 30);
    }

    let msg = '';
    if (this.paused) {
      msg = this.touch
        ? 'PAUSED  ·  tap ⏸ to resume'
        : 'PAUSED  ·  P to resume  ·  Esc to quit';
    } else if (performance.now() < this.splatUntil) {
      msg = this.splatText;
    } else if (g.isOver) {
      msg = 'GAME OVER';
    } else if (g.hint) {
      msg = g.hint;
    }
    if (msg !== this.lastKey) {
      this.lastKey = msg;
      this.message.textContent = msg;
      this.message.classList.toggle('hidden', msg === '');
    }
  }
}

function el(tag: string, cls?: string): HTMLElement {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  return e;
}
